import type { Operation } from "@keystone/core";

/**
 * Tool naming: `{service}_{action}_{resource}` snake_case, per Anthropic's mcp-builder
 * guidance. The `service` prefix is always present so cross-server collisions are
 * avoided when an agent has multiple servers connected.
 *
 * Verb canonicalization: operationId verbs like "list", "create", "update", "delete",
 * "charge", "transfer" are kept literal; less common verbs are normalized.
 */

const ACTION_ALIASES: Record<string, string> = {
  get: "get",
  retrieve: "get",
  read: "get",
  fetch: "get",
  list: "list",
  search: "search",
  query: "search",
  find: "search",
  lookup: "lookup",
  create: "create",
  add: "create",
  new: "create",
  update: "update",
  modify: "update",
  edit: "update",
  patch: "update",
  put: "update",
  replace: "update",
  delete: "delete",
  remove: "delete",
  destroy: "delete",
  charge: "charge",
  refund: "refund",
  transfer: "transfer"
};

export function snake(input: string): string {
  return input
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .toLowerCase();
}

export function serviceFromTags(op: Operation, fallback: string): string {
  const tag = op.tags[0] ?? fallback;
  return snake(tag) || "api";
}

export function actionFromOperationId(op: Operation): string {
  const id = op.id;
  const firstWord = id.match(/^[a-z]+/)?.[0]?.toLowerCase() ?? "do";
  return ACTION_ALIASES[firstWord] ?? snake(firstWord);
}

export function resourceFromPath(path: string): string {
  const segments = path.split("/").filter(Boolean).filter((s) => !s.startsWith("{"));
  if (segments.length === 0) return "root";
  // Take the last non-parameter segment; if the last is plural (cats), use singular form
  // only for `list`-style action — the action prefix conveys the cardinality.
  return snake(segments[segments.length - 1] ?? "root");
}

export function buildToolName(op: Operation, service: string, action: string, resource: string): string {
  // Compose; ensure at least one underscore overall (ToolNameSchema requires it).
  const parts = [service, action, resource].filter(Boolean);
  let name = parts.join("_");
  // MCP clients (Cursor) cap names at 60 chars. Truncate from the right at a word boundary.
  if (name.length > 60) {
    name = name.slice(0, 60).replace(/_[^_]*$/, "") || name.slice(0, 60);
  }
  // Last-resort: if name lacks an underscore, append the synthesized op id.
  if (!name.includes("_")) name = `${name}_${snake(op.id)}`;
  return name;
}
